import { Digest, element } from '../../global/index';
import { debounce } from '../../global/helpers';
import styles from './ui-slides.css';
import type {
    PaginationChangeEvent,
    ThumbnailPaginationData,
    ThumbnailsPaginationData,
} from './ui-slides.type';
import { UIElement } from '../ui-element';
import { prerenderElementsTree } from '../../global/render-api';
import { SwipeObserver } from '../../global/swipe';
import { getBaseGridPoint } from '../../global/ui-helpers';
import { UISlidesPagination } from './ui-slides-pagination';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child, Children } from '../../decorators/child.decorator';
import type { UIOffer } from '../offer/ui-offer';
import type { UIProduct } from '../products/ui-product';
import type { UIAward } from '../awards/ui-award';
import type { UIColumn } from '../columns/ui-column';

/**
 * @class
 * @memberof SharedComponents
 * @element ui-slides
 * @augments {UIElement}
 * @alias UISlides
 * @element ui-slides
 * @classdesc Represents a class for <code>ui-slides</code> element.
 * Carousel like element for ui-offers, ui-products, ui-awards, ui-columns
 * (currently supports only ui-cards as ui-column children) with two pagination solution
 * (dots and thumbnails) and includes swipe functionality.
 * Requires only <b>ui-offer</b>, <b>ui-product</b>, <b>ui-award</b> and <b>ui-column</b> children elements.
 * @property {boolean} [thumbnails] {@attr thumbnails} Thumbnails will
 * be generated for slides based on images inside ui-offers.
 * Not compatible with other elements.
 * @property {boolean} [mobileOnly=false] {@attr mobile-only}
 * Show carousel only in 'mobile' mode when media meets media query
 * mobile device breakpoint.
 * @property {HTMLDivElement} container - Shortcut to ui-slides container.
 * @property {UIOffer|UIProduct|UIAward|UIColumn} offers - Shortcut to offers collection.
 * @slot
 * @example
 * <ui-slides>
 *     <ui-offer layout="introduction" image="assets/mocks/images/slider01.jpg" thumb-text="Slide with button" image-nocrop="true">
 *         <h2>Et harum quidem rerum facilis est et expedita distinctio.</h2>
 *     </ui-offer>
 *     <ui-offer layout="introduction" image="assets/mocks/images/slider02.jpg" thumb-text="Slide with button" image-nocrop="true">
 *         <h2>Et harum quidem rerum facilis est et expedita distinctio.</h2>
 *     </ui-offer>
 *     <ui-offer layout="introduction" image="assets/mocks/images/slider03.jpg" thumb-text="Slide with button" image-nocrop="true">
 *         <h2>Et harum quidem rerum facilis est et expedita distinctio.</h2>
 *     </ui-offer>
 * </ui-slides>
 */
@Component({
    tag: 'ui-slides',
    styles,
})
export class UISlides extends UIElement {
    private readonly swiper: SwipeObserver = new SwipeObserver(this, 40, {
        lockHorizontalAxis: true,
    });
    private visibilityObserver: MutationObserver | null = null;

    private readonly swipeLeftHandler = this.onSwipeLeft.bind(this);
    private readonly swipeRightHandler = this.onSwipeRight.bind(this);
    private readonly paginationChangeHandler: (
        event: PaginationChangeEvent
    ) => void = this.onPaginationChange.bind(this);
    private readonly resizeHandler: () => void = this.onResize.bind(this);
    private readonly debouncedResizeHandler: () => void = debounce(
        this.resizeHandler,
        200
    );
    private activeIndex: number = 0;
    private slidesElementWidth: number = 0;
    private _id: string | null = null;
    private areSwipeEventHandlersAttached: boolean = false;
    private visibleOffers: Array<UIOffer | UIProduct | UIAward | UIColumn> = [];

    @Attribute(Boolean) thumbnails!: boolean;
    @Attribute(Boolean) mobileOnly!: boolean;

    @Child('.ui-slides__container') container!: HTMLElement;
    @Children('ui-offer,ui-product,ui-award,ui-column') offers!: NodeListOf<
        UIOffer | UIProduct | UIAward | UIColumn
    >;
    @Child('.ui-slides__content-wrapper') contentWrapperElement!: HTMLElement;
    @Child('ui-slides-pagination') paginationElement!: UISlidesPagination;

    /**
     * @private
     */
    private get uuid(): string {
        this._id ??= Digest.randomId();
        return this._id;
    }

    /**
     * @returns {boolean}
     * @private
     */
    private hasSlides(): boolean {
        return !(
            window.matchMedia('(min-width: 768px)').matches && this.mobileOnly
        );
    }

    /**
     * @private
     */
    private onSwipeRight(): void {
        this.paginationElement.setActiveIndex(this.activeIndex - 1);
        this.adjustSlidesContentOffset();
    }

    /**
     * @private
     */
    private onSwipeLeft(): void {
        this.paginationElement.setActiveIndex(this.activeIndex + 1);
        this.adjustSlidesContentOffset();
    }

    /**
     * Calculates and sets CSS variable for the carousel content offset.
     * getBaseGridPoint() is used for row gap value.
     * @private
     */
    private adjustSlidesContentOffset(): void {
        if (this.slidesElementWidth > 0) {
            this.style.setProperty(
                '--ui-slides-content-offset',
                `${this.activeIndex * (this.slidesElementWidth + getBaseGridPoint() * 2) * -1}px`
            );
        }
    }

    /**
     * Sliding transform transition is disabled when user prefers-reduced-motion.
     * Opacity crossfade is an accessibility friendly alt that still
     * signals slight feedback. Does nothing when reduced motion is not requested.
     * @private
     */
    private playReducedMotionTransition(): void {
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        this.contentWrapperElement.animate([{ opacity: 0.4 }, { opacity: 1 }], {
            duration: 180,
            easing: 'ease-out',
        });
    }

    /**
     * Sets up visibility detection for slides that start hidden
     * @private
     */
    private addVisibilityObserver(): void {
        if (this.slidesElementWidth > 0) {
            return;
        }

        const parent = this.parentElement;
        if (!parent) {
            return;
        }

        this.visibilityObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (
                    mutation.attributeName === 'class' &&
                    !parent.classList.contains('-hidden')
                ) {
                    this.setSlidesElementWidth();

                    if (this.slidesElementWidth > 0) {
                        this.adjustSlidesContentOffset();
                        this.removeVisibilityObserver();
                    }
                }
            }
        });

        this.visibilityObserver.observe(parent, { attributes: true });
    }

    /**
     * Cleans up the visibility observer
     * @private
     */
    private removeVisibilityObserver(): void {
        if (this.visibilityObserver) {
            this.visibilityObserver.disconnect();
            this.visibilityObserver = null;
        }
    }

    /**
     * Sets value of slidesElementWidth value
     * @private
     */
    private setSlidesElementWidth(): void {
        this.slidesElementWidth =
            this.offers[0].getBoundingClientRect().width || 0;
    }

    /**
     * Adds inert attribute for all out of the view carousel slides and
     * removes it from slides in the view.
     * @private
     */
    private toggleSlideElementsFocusCapability(): void {
        this.updateVisibleOffers();
        const elementInertAttributeAction = this.hasSlides()
            ? (element: HTMLElement): void => element.setAttribute('inert', '')
            : (element: HTMLElement): void => element.removeAttribute('inert');

        this.visibleOffers.forEach(
            (slideItemElement: UIElement, index: number): void => {
                if (index !== this.activeIndex) {
                    elementInertAttributeAction(slideItemElement);
                }
            }
        );
    }

    /**
     * Updates the collection of visible offers
     * @private
     */
    private updateVisibleOffers(): void {
        this.visibleOffers = Array.from(this.offers).filter(
            (offer: Element) => !offer.classList.contains('-hidden')
        );
    }

    /**
     * @param {PaginationChangeEvent} event
     * @private
     */
    private onPaginationChange(event: PaginationChangeEvent): void {
        this.updateVisibleOffers();

        this.visibleOffers[this.activeIndex]?.setAttribute('inert', '');
        this.activeIndex = event.detail.activeIndex;
        this.visibleOffers[this.activeIndex]?.removeAttribute('inert');

        this.adjustSlidesContentOffset();
        this.playReducedMotionTransition();
    }

    /**
     * @private
     */
    private onResize(): void {
        this.setSlidesElementWidth();
        this.classList.add('-static');
        this.adjustSlidesContentOffset();

        window.requestAnimationFrame(() => {
            this.classList.remove('-static');
        });

        this.toggleSwipeEventListeners();
        this.toggleSlideElementsFocusCapability();
    }

    /**
     * @private
     */
    private addSwipeEventHandlers(): void {
        if (this.areSwipeEventHandlersAttached) {
            return;
        }
        this.areSwipeEventHandlersAttached = true;
        this.addEventListener('swipe-left', this.swipeLeftHandler);
        this.addEventListener('swipe-right', this.swipeRightHandler);
    }

    /**
     * @private
     */
    private removeSwipeEventHandlers(): void {
        if (!this.areSwipeEventHandlersAttached) {
            return;
        }
        this.areSwipeEventHandlersAttached = false;
        this.removeEventListener('swipe-left', this.swipeLeftHandler);
        this.removeEventListener('swipe-right', this.swipeRightHandler);
    }

    /**
     * @private
     */
    private toggleSwipeEventListeners(): void {
        if (this.hasSlides()) {
            this.addSwipeEventHandlers();
            return;
        }

        this.removeSwipeEventHandlers();
    }

    /**
     * @private
     */
    private addEventListeners(): void {
        this.paginationElement.addEventListener(
            'pagination-change',
            this.paginationChangeHandler as EventListener
        );
        window.addEventListener('resize', this.debouncedResizeHandler);
        this.addSwipeEventHandlers();
    }

    /**
     * @private
     */
    private removeEventListeners(): void {
        this.paginationElement.removeEventListener(
            'pagination-change',
            this.paginationChangeHandler as EventListener
        );
        window.removeEventListener('resize', this.debouncedResizeHandler);
        this.removeSwipeEventHandlers();
    }

    /**
     * If the pagination has thumbnail buttons this is used
     * to extract the data for thumbnail buttons from the carousel slides.
     * @private
     */
    private parseThumbnailsPaginationData(): ThumbnailsPaginationData {
        return [].map.call(
            this.offers,
            (
                slideItemElement: HTMLElement & { image?: string }
            ): ThumbnailPaginationData => {
                const slideItemImageElement: HTMLImageElement | null =
                    slideItemElement.querySelector('img');
                const slideItemImageElementSrc: string | null =
                    slideItemImageElement &&
                    (slideItemImageElement.getAttribute('data-src') ??
                        slideItemImageElement.getAttribute('src'));
                const thumbnailSrc: string =
                    slideItemImageElementSrc ?? slideItemElement.image ?? '';
                const thumbnailTitle: string =
                    slideItemElement.getAttribute('thumb-text') ?? '';

                return {
                    thumbnailSrc,
                    thumbnailTitle,
                };
            }
        ) as ThumbnailsPaginationData;
    }

    /**
     * @inheritDoc
     */
    render(): void {
        const isMobileOnlyMandatory: boolean =
            this.querySelectorAll('ui-offer, ui-product').length === 0;
        isMobileOnlyMandatory && this.setAttribute('mobile-only', '');

        const isThumbnailsAllowed: boolean =
            this.querySelectorAll('ui-offer').length !== 0;
        this.updateVisibleOffers();
        const slideItemElementsCount: number = this.visibleOffers.length;
        slideItemElementsCount < 2 && this.setAttribute('nocontent', '');

        const slideItemElements: Array<UIElement> = [].map.call(
            this.offers,
            (slideItemElement: UIElement) => {
                slideItemElement.remove();
                return slideItemElement;
            }
        ) as Array<UIElement>;

        window.requestAnimationFrame(() => {
            this.toggleSlideElementsFocusCapability();
        });

        const paginationElement: UISlidesPagination = element`<ui-slides-pagination
        items="${slideItemElementsCount}"
        thumbnails="${isThumbnailsAllowed && this.thumbnails}"
        container-id="${this.uuid}"
      ></ui-slides-pagination>`;

        prerenderElementsTree(paginationElement, true);

        this.append(element`<div id="${this.uuid}" class="ui-slides__container">
      <div class="ui-slides__content-wrapper">
        <div class="ui-slides__content">
          ${slideItemElements}
        </div>
      </div>
      ${paginationElement}
    </div>`);

        if (isThumbnailsAllowed && this.thumbnails) {
            const thumbnailsPaginationData: ThumbnailsPaginationData =
                this.parseThumbnailsPaginationData();

            this.paginationElement.setThumbnailsPaginationData(
                thumbnailsPaginationData
            );
        }
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.addEventListeners();
        this.addVisibilityObserver();
        this.resizeHandler();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.removeEventListeners();
        this.removeVisibilityObserver();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.addEventListeners();
        this.addVisibilityObserver();
        this.resizeHandler();
    }
}
